import React, { useRef, useState } from 'react';
import { addVariant, CartVariantData, getCartData } from '../../../utils/cart';

import './quick-add-styles.scss';
import Tooltip from '../../_shared/ui-components/Tooltip';
import PurchaseLimitMessaging, {
  hasMetLimitedProductCartQuantity,
  PurchaseLimitTagDataTypes,
} from '../../_shared/ui-components/PurchaseLimitItem/PurchaseLimitMessaging';
import { hasHiddenInventoryTag } from 'src/scripts/utils/plp-pdp-shared';
import { getIdFromShopifyGraphQLId } from 'src/scripts/utils/shopify';
import { checkIfItemCanBeSoldOut } from 'src/scripts/utils/helpers';
import styles from '../../_shared/ui-components/Tooltip/styles.module.scss';
import { PlpProductsExtendedQuery } from './GetExtendedProduct.shopify.generated';
export interface QuickAddPanelTypes {
  shouldShowATCmodal?: boolean;
  extendedData: PlpProductsExtendedQuery;
  price: number;
  compareAtPrice: number | null;
  title?: string;
  soldOutCopy?: string;
  ga4options: ga4options;
  ATBtype: string;
  purchaseLimitTagData?: PurchaseLimitTagDataTypes;
  quickFitSizeSelectorText?: IQuickFitSizeSelectorData | undefined;
}

interface IQuickFitSizeSelectorData {
  value: string;
}
export interface ga4options {
  index: number;
  collectionHandle: string;
  collectionShopifyId: string;
}

const QuickAddPanel = ({
  extendedData,
  price,
  compareAtPrice,
  soldOutCopy,
  ga4options,
  ATBtype,
  purchaseLimitTagData,
}: QuickAddPanelTypes) => {
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  if (!extendedData?.productByHandle) return null;
  const { tags, availableForSale, variants, productType } = extendedData.productByHandle;

  const areAllSizesSoldOut = checkIfItemCanBeSoldOut(tags) ? !availableForSale : false;
  const shouldHideInventory = hasHiddenInventoryTag(tags);
  const shouldShowQuickAddText = shouldHideInventory ? false : !areAllSizesSoldOut;
  const sizesData = variants.edges.map((edge) => {
    return {
      isSizeAvailable: edge.node.availableForSale,
      value: edge.node.selectedOptions.find((item) => item.name === 'Size')?.value,
      variantId: getIdFromShopifyGraphQLId(edge.node.id),
      sku: edge.node.sku,
    };
  });

  const addToCart = (variantId: string, variantPricesData: CartVariantData) => {
    setIsAddedToCart(true);
    addVariant(variantId, variantPricesData, { ATBtype }, ga4options);
    setTimeout(() => {
      setIsAddedToCart(false);
    }, 1000);
  };

  const isSneakers = productType === 'Accessories:Shoes:Sneakers';

  return sizesData && sizesData.length > 0 ? (
    <div style={sizesData.length === 1 ? { justifyContent: 'center' } : {}} className={`quick-add quick-add-dark-mode`}>
      {isAddedToCart ? (
        <p style={{ lineHeight: '29px' }} className={`mx-auto quick-add-text text-center`}>
          Added!
        </p>
      ) : (
        <>
          {sizesData.length > 1 && shouldShowQuickAddText ? (
            <p className={`mb-0 quick-add-text`}>Quick add:</p>
          ) : sizesData.length > 1 ? (
            <p style={{ opacity: 0.5 }} className={`mb-0 quick-add-text`}>
              {soldOutCopy}
            </p>
          ) : null}
          <div className={`quick-add-sizes ${isSneakers ? 'sneaker-sizes' : ''}`}>
            {sizesData?.map((size) => (
              <SizeButton
                key={size.variantId}
                size={size}
                isSingleSize={sizesData.length === 1}
                price={price}
                extendedData={extendedData.productByHandle}
                compareAtPrice={compareAtPrice}
                addToCart={addToCart}
                sku={size.sku}
                purchaseLimitTagData={purchaseLimitTagData}
                shouldHideInventory={shouldHideInventory}
              />
            ))}
          </div>
        </>
      )}
    </div>
  ) : null;
};

export default QuickAddPanel;

const SizeButton = ({
  size,
  price,
  compareAtPrice,
  extendedData,
  addToCart,
  isSingleSize,
  sku,
  purchaseLimitTagData,
  shouldHideInventory,
}: {
  size: {
    isSizeAvailable: boolean;
    value: string | undefined;
    variantId: string;
  };
  price: number;
  compareAtPrice: number | null;
  extendedData: PlpProductsExtendedQuery['productByHandle'];
  isSingleSize: boolean;
  sku: string | null | undefined;
  purchaseLimitTagData?: PurchaseLimitTagDataTypes;
  addToCart: (variantId: string, variantPricesData: CartVariantData) => void;
  shouldHideInventory?: boolean;
}) => {
  const tooltipPlacement = useRef<HTMLButtonElement | null>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  if (!extendedData) return null;

  const variantPricesData = {
    id: Number(size.variantId),
    price,
    displayPrice: compareAtPrice,
    tags: extendedData?.tags,
    productId: Number(getIdFromShopifyGraphQLId(extendedData.id)),
  };

  const cartData = getCartData();
  const hasMetATCLimit =
    purchaseLimitTagData && sku ? hasMetLimitedProductCartQuantity(cartData, sku, purchaseLimitTagData) : undefined;

  const itemCantBeAdded = !size.isSizeAvailable || hasMetATCLimit || shouldHideInventory;

  return (
    <>
      <button
        key={size.value}
        type="button"
        ref={tooltipPlacement}
        data-disabled={itemCantBeAdded ? true : false}
        data-variant={size.variantId}
        className={`quick-add-${
          isSingleSize ? 'translate-enabled ' + (itemCantBeAdded ? 'sold-out' : 'add-to-bag') : 'size-btn'
        }`}
        onMouseEnter={() => {
          if (itemCantBeAdded) {
            setIsTooltipOpen(true);
          }
        }}
        onMouseLeave={() => {
          if (itemCantBeAdded) {
            setIsTooltipOpen(false);
          }
        }}
        onClick={() => {
          if (!itemCantBeAdded) addToCart(size.variantId, variantPricesData);
        }}
      >
        {isSingleSize && size.isSizeAvailable && !shouldHideInventory
          ? 'add to bag'
          : isSingleSize
          ? 'Sold Out Online'
          : size.value}
      </button>
      {itemCantBeAdded && !isSingleSize && tooltipPlacement.current ? (
        <Tooltip
          key={size.variantId}
          children={
            hasMetATCLimit && purchaseLimitTagData ? (
              <PurchaseLimitMessaging purchaseLimitTagData={purchaseLimitTagData} messageType={'variant'} />
            ) : (
              <p className={`body ${styles.tooltip}`}>Sold Out Online</p>
            )
          }
          isOpen={isTooltipOpen}
          anchorOriginProps={{ horizontal: 'left', vertical: 'bottom' }}
          transformOriginProps={{ vertical: 'top', horizontal: 'left' }}
          anchorElement={tooltipPlacement.current}
          isMouseHover={true}
        ></Tooltip>
      ) : null}
    </>
  );
};
